import { Injectable, NgZone } from '@angular/core';
import { Subject } from 'rxjs';

export type NotificationType = 'success' | 'error' | 'info' | 'warning';

export interface NotificationData {
  type: NotificationType;
  message: string;
}

/** Delay after MatDialog.close() so the toast is not hidden by cdk-overlay. */
export const NOTIFICATION_AFTER_DIALOG_MS = 350;

export interface NotificationShowOptions {
  deferMs?: number;
  /** Show even while logout is silencing leftover Firestore errors. */
  force?: boolean;
}

function resolveDeferMs(
  type: NotificationType,
  options?: NotificationShowOptions,
): number {
  if (options?.deferMs !== undefined) {
    return options.deferMs;
  }
  return type === 'success' ? NOTIFICATION_AFTER_DIALOG_MS : 0;
}
@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private notificationSubject = new Subject<NotificationData>();
  notification$ = this.notificationSubject.asObservable();
  private errorToastsMuted = false;

  constructor(private ngZone: NgZone) {}

  /** Hide permission/load errors from live listeners after sign-out. */
  muteErrorToasts(): void {
    this.errorToastsMuted = true;
  }

  unmuteErrorToasts(): void {
    this.errorToastsMuted = false;
  }

  show(
    type: NotificationType,
    message: string,
    options?: NotificationShowOptions,
  ): void {
    const resolvedType: NotificationType =
      type === 'error' && this.isSomethingWentWrongMessage(message)
        ? 'warning'
        : type;
    if (resolvedType === 'error' && this.errorToastsMuted && !options?.force) {
      return;
    }
    const deferMs = resolveDeferMs(resolvedType, options);
    const emit = () => {
      this.ngZone.run(() => {
        this.notificationSubject.next({ type: resolvedType, message });
      });
    };

    if (deferMs > 0) {
      setTimeout(emit, deferMs);
    } else {
      emit();
    }
  }

  showAfterDialog(type: NotificationType, message: string): void {
    this.show(type, message, { deferMs: NOTIFICATION_AFTER_DIALOG_MS });
  }

  showSuccess(message: string, options?: NotificationShowOptions): void {
    this.show('success', message, options);
  }

  showError(message: string, options?: NotificationShowOptions): void {
    this.show('error', message, options);
  }

  showWarning(message: string, options?: NotificationShowOptions): void {
    this.show('warning', message, options);
  }

  private isSomethingWentWrongMessage(message: string): boolean {
    return /^\s*something went wrong/i.test(message || '');
  }
}
